import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import CustomModal from "../../widgets/CustomModal";
import CustomBtn from "../../widgets/CustomBtn";
import InfiniteScroll from "../../services/InfiniteScroll";
import {
  addCampus,
  campusList,
  deleteCampus,
  updateCampus,
} from "../../_slice/campusSlice";
import AppContext from "../../context/AppContext";
import editImg from "../../../assets/images/edit.png";
import deleteImg from "../../../assets/images/delete.png";

function Campus() {
  const [modalShow, setModalShow] = useState({ show: false, id: "" });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    id: "",
    name: "",
  });
  const [checkedState, setCheckedState] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm({
    mode: "onChange",
    defaultValues: { name: "", address: "" },
  });

  // Redux selector to get campus data and status
  const { campusData, status: campusStatus } = useSelector(
    (state) => state.campus
  );

  // Effect to fetch campuses initially or on status change
  useEffect(() => {
    if (campusStatus === "idle") {
      fetchMoreCampuses(0);
    }
  }, [campusStatus, dispatch, setLoader]);

  // Function to fetch more campuses
  const fetchMoreCampuses = (resetPage) => {
    const pageCount = resetPage > 0 ? resetPage : page;

    dispatch(campusList({ pageCount, navigate, setLoader }))
      .then((response) => {
        if (response?.payload?.campuses?.data?.length > 0) {
          setPage(response?.payload?.campuses?.current_page + 1);
          setHasMore(
            response?.payload?.campuses?.current_page <
            response?.payload?.campuses?.last_page
          );
        } else {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching more campuses:", error);
      });
  };

  // Handle checkbox change event
  const handleCheckboxChange = (event, id, name) => {
    const checked = event.target.checked;
    setCheckedState((prevState) => ({
      ...prevState,
      [id]: checked,
    }));
    if (checked) {
      const newState = {
        campus: { campId: id, campName: name },
        orgId: location?.state?.orgId || "",
      };
      navigate("/organization/locations", { state: newState });
    }
  };

  // Function to create a new campus
  const createCampus = (data) => {
    dispatch(addCampus({ data, navigate, setLoader })).then(() => {
      setModalShow({ show: false, id: "" });
      fetchMoreCampuses(1);
    });
  };

  // Function to edit an existing campus
  const editCampus = (data) => {
    dispatch(updateCampus({ id: modalShow.id, data, navigate, setLoader })).then(() => {
      setModalShow({ show: false, id: "" });
    });
  };

  // Function to remove a campus
  const removeCampus = (id) => {
    dispatch(deleteCampus({ id, navigate, setLoader })).then(() => {
      fetchMoreCampuses(1);
    });
    setDeleteModal({ show: false });
  };

  // Reset form values when modalShow changes
  useEffect(() => {
    if (!modalShow.show) {
      setValue("name", "");
      setValue("address", "");
      clearErrors();
    }
  }, [modalShow.show, setValue, clearErrors]);

  return (
    <>
      <div className='org'>
        <div className='org_header d-flex align-items-center justify-content-between'>
          <h1 className='org_header_heading'>Campus</h1>
          <button
            type='button'
            className='submit_btn'
            onClick={() => setModalShow({ show: true })}
          >
            + Add Campus
          </button>
        </div>
        <InfiniteScroll
          fetchMoreData={() => fetchMoreCampuses(0)}
          hasMore={hasMore}
          isLoading={campusStatus === 'loading'}
          threshold={200}
        >
          <div className='org_table table-responsive table-border-0'>
            <table className='table'>
              <thead>
                <tr>
                  <td className='org_table_column w-5 zone_table_heading'> </td>
                  <td className='org_table_column w-40 zone_table_heading'>Campus Name</td>
                  <td className='org_table_column w-40 zone_table_heading'>Address</td>
                  <td className='org_table_column w-40 text-center zone_table_heading'>Action</td>
                </tr>
              </thead>
              <tbody>
                {campusData?.data?.map(item => (
                  <tr key={`Campus_${item.id}`}>
                    <td className='org_table_row w-5 zone_body_text'>
                      <label className='check_box ml-2' htmlFor={`checkbox-${item.id}`}>
                        <input
                          type='checkbox'
                          id={`checkbox-${item.id}`}
                          checked={checkedState[item.id] || false}
                          onChange={(e) => handleCheckboxChange(e, item.id, item.name)}
                        />
                        <span className='checkbox_check'></span>
                      </label>
                    </td>
                    <td className='org_table_row w-40 zone_body_text'>{item.name}</td>
                    <td className='org_table_row w-40 zone_body_text'>{item.address}</td>
                    <td className='org_table_row w-40 zone_body_text'>
                      <div className='d-flex align-items-center justify-content-center'>
                        <button
                          type='button'
                          className='action_btns mr-4'
                          onClick={() => {
                            setModalShow({ show: true, id: item.id });
                            setValue('name', item.name);
                            setValue('address', item.address);
                          }}
                        >
                          <img src={editImg} alt='' className='mr-1' />
                          Edit
                        </button>
                        <button
                          type='button'
                          className='action_btns'
                          onClick={() => setDeleteModal({ show: true, id: item.id, name: item.name })}
                        >
                          <img src={deleteImg} alt='' className='mr-1' />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </InfiniteScroll>
      </div>

      {/* Modal for adding/editing campus */}
      <CustomModal
        show={modalShow.show}
        onHide={() => setModalShow({ show: false })}
        heading={modalShow.id ? 'Edit Campus' : 'Add Campus'}
        size="lg"
        body={
          <form
            onSubmit={
              modalShow.id
                ? handleSubmit(editCampus)
                : handleSubmit(createCampus)
            }
          >
            <div className='row'>
              <div className='col-lg-6'>
                <div className='form-group'>
                  <label className='innerlable'>Campus Name</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='type here'
                    {...register('name', { required: 'Campus name required' })}
                  />
                  {errors.name && (
                    <p className='text-danger'>{errors.name.message}</p>
                  )}
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='form-group'>
                  <label className='innerlable mb_top'>Campus Address (Optional)</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='type here'
                    {...register('address')}
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <CustomBtn
                  buttonText='Save'
                  className='mt-5'
                  type='submit'
                  id='campusName'
                />
              </div>
            </div>
          </form>
        }
      />

      {/* Modal for confirming campus deletion */}
      <CustomModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false })}
        size="md"
        body={
          <div className='deleteModal'>
            <p className='text-center mb-5'>Are you sure, you want to delete this {deleteModal.name}?</p>
            <div className='d-flex justify-content-between align-items-center custom_bottom_padding'>
              <button type='button' className='submit_btn' onClick={() => removeCampus(deleteModal.id)}>Yes</button>
              <button type='button' className='cancleBtn' onClick={() => setDeleteModal({ show: false })}>No</button>
            </div>
          </div>
        }
      />
    </>
  );
}

export default Campus;